/**
 * SSE client for the portal-chat stream.
 *
 * Uses `fetch` with a ReadableStream reader so we get the Authorization
 * cookie + same proxy-routing as every other portal API call. Native
 * `EventSource` would force GET — we need POST with a JSON body.
 *
 * Parses `data: {...}\n\n` event frames and invokes the caller's
 * handler for each one.
 */
import type { ActiveContext, ChatMessage, ChatStreamEvent } from './types';

const SSE_URL = '/api/chat/stream';

export interface PostChatTurnArgs {
  messages: ChatMessage[];
  activeContext: ActiveContext;
  signal?: AbortSignal;
  onEvent: (evt: ChatStreamEvent) => void;
}

export async function postChatTurn({
  messages,
  activeContext,
  signal,
  onEvent,
}: PostChatTurnArgs): Promise<void> {
  const resp = await fetch(SSE_URL, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages, active_context: activeContext }),
    signal,
  });

  if (!resp.ok || !resp.body) {
    const text = await resp.text().catch(() => '');
    throw new Error(`chat HTTP ${resp.status}: ${text || resp.statusText}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  // Frames are separated by a blank line per the SSE spec.
  // Each frame is one or more `data: <json>` lines.
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sep: number;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      const dataLine = frame
        .split('\n')
        .map((l) => l.trim())
        .find((l) => l.startsWith('data:'));
      if (!dataLine) continue;
      const json = dataLine.slice(5).trim();
      if (!json) continue;
      try {
        const evt = JSON.parse(json) as ChatStreamEvent;
        onEvent(evt);
      } catch (err) {
        // Drop malformed frames; surface as a single error event.
        onEvent({ type: 'error', message: `bad SSE frame: ${(err as Error).message}` });
      }
    }
  }
}
